/**
 * Animal radar — SVG port of plumatotm_radar.py (_create_radar_chart).
 *
 * API:
 *   renderAnimalRadar(container, radar, { axes, iconsBaseUrl })
 *
 * radar  = one item from /analyze radar_data.radars
 * axes   = radar_data.axes (13 bodies, Sun at 12 o'clock, clockwise)
 *
 * Matplotlib polar: theta=0 at North, clockwise, ylim 0–130.
 * Icons sit just outside the 100% ring. Node area ∝ planet weight.
 */
(function (global) {
  "use strict";

  const NS = "http://www.w3.org/2000/svg";
  const XLINK = "http://www.w3.org/1999/xlink";

  const canvasSize = 800;
  const center = { x: 400, y: 400 };
  const rMaxData = 130;
  const rDraw = 320;
  const scale = rDraw / rMaxData;
  const maxRadius = 100;
  const iconPad = 0.13;
  const planetIconSize = 73;
  const angleIconSize = 46;
  const spokeWidth = 2;
  const polyWidth = 3;

  const DEFAULT_AXES = [
    "Sun",
    "Ascendant",
    "Moon",
    "Mercury",
    "Venus",
    "Mars",
    "Jupiter",
    "Saturn",
    "Uranus",
    "Neptune",
    "Pluto",
    "MC",
    "North Node",
  ];

  const NODE_WEIGHT = {
    Sun: 23.0,
    Ascendant: 18.0,
    Moon: 15.0,
    Mercury: 7.0,
    Venus: 6.0,
    Mars: 6.0,
    Jupiter: 5.0,
    Saturn: 5.0,
    Uranus: 3.0,
    Neptune: 3.0,
    Pluto: 2.0,
    "North Node": 2.0,
    MC: 5.0,
  };

  const ICON_FILES = {
    Sun: "PLANETS/SUN_BOLD.svg",
    Moon: "PLANETS/MOON_BOLD.svg",
    Mercury: "PLANETS/MERCURY_BOLD.svg",
    Venus: "PLANETS/VENUS_BOLD.svg",
    Mars: "PLANETS/MARS_BOLD.svg",
    Jupiter: "PLANETS/JUPITER_BOLD.svg",
    Saturn: "PLANETS/SATURN_BOLD.svg",
    Uranus: "PLANETS/URANUS_BOLD.svg",
    Neptune: "PLANETS/NEPTUNE_BOLD.svg",
    Pluto: "PLANETS/PLUTO_BOLD.svg",
    "North Node": "PLANETS/NORTH_NODE_BOLD.svg",
    Ascendant: "PLANETS/AC_S90.svg",
    MC: "PLANETS/MC_S90.svg",
  };

  function el(tag, attrs, parent) {
    const node = document.createElementNS(NS, tag);
    if (attrs) {
      for (const [k, v] of Object.entries(attrs)) {
        if (v === undefined || v === null) continue;
        if (k === "href") {
          node.setAttributeNS(XLINK, "href", v);
          node.setAttribute("href", v);
        } else {
          node.setAttribute(k, String(v));
        }
      }
    }
    if (parent) parent.appendChild(node);
    return node;
  }

  function axisAngle(index, n) {
    return (index * 2 * Math.PI) / n;
  }

  function polar(angle, r) {
    const px = r * scale;
    return {
      x: center.x + px * Math.sin(angle),
      y: center.y - px * Math.cos(angle),
    };
  }

  function nodeRadius(planet) {
    const weight = NODE_WEIGHT[planet] == null ? 5.0 : NODE_WEIGHT[planet];
    const surface = Math.max(50, (weight / 23.0) * 900);
    return Math.sqrt(surface / Math.PI);
  }

  function iconUrl(baseUrl, name, cacheBust) {
    const file = ICON_FILES[name];
    if (!file) return null;
    const base = String(baseUrl || "").replace(/\/?$/, "/");
    const encoded = file
      .split("/")
      .map((seg) => encodeURIComponent(seg))
      .join("/");
    let url = base + encoded;
    if (cacheBust) {
      url +=
        (url.includes("?") ? "&" : "?") +
        "v=" +
        encodeURIComponent(String(cacheBust));
    }
    return url;
  }

  /**
   * @param {HTMLElement|string} container
   * @param {object} radar — radar_data.radars[i]
   * @param {{
   *   axes?: string[],
   *   iconsBaseUrl?: string,
   *   iconsCacheBust?: string
   * }} [options]
   * @returns {SVGSVGElement}
   */
  function renderAnimalRadar(container, radar, options) {
    const root =
      typeof container === "string"
        ? document.querySelector(container)
        : container;
    if (!root) throw new Error("renderAnimalRadar: container not found");
    const opts = options || {};
    const axes = opts.axes && opts.axes.length ? opts.axes : DEFAULT_AXES;
    const iconsBaseUrl =
      opts.iconsBaseUrl || "https://images.plumastro.com/birthcharticons";
    const iconsCacheBust = opts.iconsCacheBust || "";
    const values = (radar && radar.values) || {};

    root.innerHTML = "";
    const svg = el(
      "svg",
      {
        viewBox: `0 0 ${canvasSize} ${canvasSize}`,
        width: "100%",
        height: "100%",
        xmlns: NS,
        style: "display:block;background:transparent;",
      },
      root
    );

    const n = axes.length;
    const pts = [];
    for (let i = 0; i < n; i++) {
      const angle = axisAngle(i, n);
      const v = Number(values[axes[i]]);
      const r = Number.isFinite(v) ? Math.max(0, v) : 0;
      pts.push({ angle, r, planet: axes[i] });
    }

    for (const p of pts) {
      const end = polar(p.angle, maxRadius);
      el(
        "line",
        {
          x1: center.x,
          y1: center.y,
          x2: end.x,
          y2: end.y,
          stroke: "black",
          "stroke-width": spokeWidth,
          "stroke-opacity": 0.8,
        },
        svg
      );
    }

    const d = pts
      .map((p, i) => {
        const xy = polar(p.angle, p.r);
        return `${i === 0 ? "M" : "L"} ${xy.x} ${xy.y}`;
      })
      .join(" ");
    el(
      "path",
      {
        d: d + " Z",
        fill: "none",
        stroke: "black",
        "stroke-width": polyWidth,
        "stroke-linejoin": "round",
        "stroke-opacity": 0.9,
      },
      svg
    );

    for (const p of pts) {
      const xy = polar(p.angle, p.r);
      el(
        "circle",
        {
          cx: xy.x,
          cy: xy.y,
          r: nodeRadius(p.planet),
          fill: "black",
        },
        svg
      );
    }

    const iconR = maxRadius + iconPad * rMaxData;
    for (const p of pts) {
      const xy = polar(p.angle, iconR);
      const url = iconUrl(iconsBaseUrl, p.planet, iconsCacheBust);
      const size =
        p.planet === "Ascendant" || p.planet === "MC"
          ? angleIconSize
          : planetIconSize;
      if (url) {
        el(
          "image",
          {
            href: url,
            x: xy.x - size / 2,
            y: xy.y - size / 2,
            width: size,
            height: size,
            preserveAspectRatio: "xMidYMid meet",
          },
          svg
        );
      } else {
        el(
          "circle",
          { cx: xy.x, cy: xy.y, r: 4, fill: "black" },
          svg
        );
      }
    }

    return svg;
  }

  global.renderAnimalRadar = renderAnimalRadar;
  if (typeof module !== "undefined" && module.exports) {
    module.exports = { renderAnimalRadar };
  }
})(typeof window !== "undefined" ? window : globalThis);
